import * as THREE from 'three'

import { MAX_LIGHT_LEVEL } from './chunks'

import CustomShaderMaterial from '../libs/three-custom-shader-material'

const ALPHA_TEST = 0.5

export class AtlasMaterial {
  constructor({ anisotropy, skyUniforms }) {
    this.anisotropy = anisotropy
    this.skyUniforms = skyUniforms

    // this.minFilter = THREE.NearestFilter
    // this.minFilter = THREE.NearestMipMapNearestFilter
    this.minFilter = THREE.NearestMipMapLinearFilter
    // this.minFilter = THREE.LinearFilter
    // this.minFilter = THREE.LinearMipMapLinearFilter
    // this.minFilter = THREE.LinearMipMapNearestFilter

    this.width = 256
    this.height = 256
    this.pad = 0
    this.maxSlices = 1

    this.lookupTex = new THREE.DataTexture(
      new Float32Array(this.maxSlices * 4),
      this.maxSlices,
      1,
      THREE.RGBAFormat,
      THREE.FloatType
    )
    this.lookupTex.needsUpdate = true
    this.lookupTex.minFilter = THREE.NearestFilter
    this.lookupTex.magFilter = THREE.NearestFilter
    this.lookupTex.wrapS = THREE.ClampToEdgeWrapping
    this.lookupTex.wrapT = THREE.ClampToEdgeWrapping

    // this.canvases = [new OffscreenCanvas(this.width, this.height)]
    this.canvases = [document.createElement('canvas')]
    this.ctxs = this.canvases.map(canvas => canvas.getContext('2d'))

    this.texture = new THREE.Texture(this.canvases[0])
    // this.texture.premultiplyAlpha = true // important with Canvas 2D + sRGB
    this.texture.flipY = false // no-flip, we work in image space everywhere
    // this.texture.needsUpdate = true // ???
    this.texture.generateMipmaps = false
    this.texture.mipmaps = []
    this.texture.anisotropy = this.anisotropy
    this.texture.magFilter = THREE.NearestFilter
    this.texture.minFilter = this.minFilter
    this.texture.wrapS = this.texture.wrapT = THREE.ClampToEdgeWrapping
    this.texture.colorSpace = THREE.SRGBColorSpace

    this.textureLoader = new THREE.TextureLoader()
    this.skyLUT = this.textureLoader.load('/skyLUT.png', tex => {
      tex.magFilter = THREE.LinearFilter
      tex.minFilter = THREE.LinearFilter
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping
      tex.colorSpace = THREE.SRGBColorSpace
      tex.needsUpdate = true
      this.uniforms.uSkyLUTSize.value.set(tex.source.data.width, tex.source.data.height)
    })

    this.uniforms = {
      ...skyUniforms, // uElapsed, uTime, uSkyLUT, uSkyLutSize, uWindStrength, uWindDirection
      uAtlas: { value: this.texture },
      uAtlasSize: { value: new THREE.Vector2(this.width, this.height) },
      uLookupTex: { value: this.lookupTex },
      uLookupWidth: { value: this.maxSlices },
      uPadPx: { value: this.pad },
      uColorOverride: { value: new THREE.Vector4() },
      uColorOverrideEnabled: { value: 0 },
    }

    // atlas material
    this.base = new THREE.MeshBasicMaterial({
      // color: 'white',
      // map: this.texture,
      // alphaMap: this.texture,
      // transparent: true,
      side: THREE.FrontSide,
      // side: THREE.DoubleSide,
      shadowSide: THREE.BackSide,
      alphaTest: ALPHA_TEST,
      // emissiveIntensity: 100,
      // toneMapped: false,
      // alphaToCoverage: true,
    })
    this.base = new CustomShaderMaterial({
      baseMaterial: this.base,
      uniforms: {
        ...this.uniforms,
      },
      vertexShader: `
        uniform vec4 uColorOverride;
        uniform float uColorOverrideEnabled;

        uniform float uElapsed;
        uniform float uWindStrength;
        uniform vec2 uWindDirection;

        varying float vTileId;
        varying vec2 vUv;
        varying vec4 vLight;
        varying float vAo;
        varying float vDim;
        varying float vWind;
        
        attribute float tileId;
        // attribute vec2 uv; // already declared
        attribute vec4 light;
        attribute float ao;
        attribute float dim;
        attribute float wind;

        void main() {
          vTileId = tileId;
          vUv = uv;
          
          
          // todo: use a defines so we dont branch
          if (uColorOverrideEnabled > 0.5) {
            vLight = uColorOverride;
            } else {
              vLight = light;
          }
          
          vAo = ao;
          vDim = dim;

          vWind = wind / 255.0;

          // wind displacement
          if (vWind > 0.0) {
            // use world position for phase variation (so adjacent blocks don't sync)
            vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
            
            // phase based on world XZ so neighboring grass sways differently
            float phase = worldPos.x * 0.5 + worldPos.z * 0.3 + uElapsed * 2.0;
            
            // main sway
            float sway = sin(phase) * 0.5 + sin(phase * 2.3 + 0.5) * 0.25;
            
            // apply displacement scaled by wind weight
            vec2 offset = normalize(uWindDirection) * sway * uWindStrength * vWind;
            
            csm_Position = position + vec3(offset.x, 0.0, offset.y);
          }
        }
      `,
      fragmentShader: `
        precision highp float;
        precision highp int;

        uniform float uTime;
        uniform sampler2D uSkyLUT;
        uniform vec2 uSkyLUTSize;
        uniform sampler2D uAtlas;
        uniform vec2 uAtlasSize;
        uniform sampler2D uLookupTex;
        uniform float uLookupWidth;
        uniform float uPadPx;

        varying float vTileId;
        varying vec2 vUv;
        varying vec4 vLight;
        varying float vAo;
        varying float vDim;

        vec4 fetchRect(float id) {
          // round to nearest integer slice
          float slice = floor(id + 0.5);
          float x = (slice + 0.5) / uLookupWidth;
          return texture2D(uLookupTex, vec2(x, 0.5)); 
        }

        vec3 sampleSky(float t) {
          // texture is 64x4, bottom row holds the sky color keys
          // horizontal: move through texel centers, let LinearFilter blend neighbors
          float u = (fract(t) * uSkyLUTSize.x + 0.5) / uSkyLUTSize.x; 
          // vertical: bottom row center
          // float v = (0.5) / uSkyLUTSize.y; 
          float v = (uSkyLUTSize.y - 0.5) / uSkyLUTSize.y;
          vec3 c = texture2D(uSkyLUT, vec2(u, v)).rgb;
          return c;
        }

        // sRGB -> Linear helper
        vec3 srgbToLinear(vec3 c) {
          bvec3 cutoff = lessThanEqual(c, vec3(0.04045));
          vec3  lower  = c / 12.92;
          vec3  higher = pow((c + 0.055) / 1.055, vec3(2.4));
          return mix(higher, lower, vec3(cutoff));
        }

        // fold derivatives into [-0.5,+0.5] tile space so LOD doesn't explode at wraps
        vec2 wrapDeriv(vec2 d){ return d - floor(d + 0.5); }

        void main() {

          vec4 rect = fetchRect(vTileId); // rect = (u0, v0, du, dv) in image space (includes padding if any)
          vec2 uv = vUv;
          
          vec2 tilePixels = rect.zw * uAtlasSize;
          vec2 halfPx = 0.5 / tilePixels;
          
          // Clamp to half-pixel inset
          vec2 safeUV = clamp(uv, halfPx, 1.0 - halfPx);
          vec2 atlasUV = rect.xy + rect.zw * safeUV;
          
          vec4 mainColor = texture(uAtlas, atlasUV);

          // debug lighting (white)
          // mainColor = vec4(1.0, 1.0, 1.0, 1.0);
          
          if (mainColor.a < ${ALPHA_TEST}) discard;

          float maxBrightness = float(${MAX_LIGHT_LEVEL});
          float globalBrightness = 1.0; // todo: remove?

          // AO should always modulate visible color, even when light == 0
          float aoStrength = 0.6;
          float aoFactor   = mix(1.0, vAo, aoStrength);

          // // ao 2: fade AO by light level so it doesnt crush shadows that are already dark
          // float n = clamp(light / maxBrightness, 0.0, 1.0);
          // float aoStrength = mix(0.4, 0.6, n);  // less AO in the dark, more in bright
          // float aoFactor   = mix(1.0, vAo, aoStrength);

          // Scale sky (was white) by tint * intensity, modulated by skylight level
          vec3 skyRGB = sampleSky(uTime);
          
          // skyRGB comes in linear, but our skyLevel (0-15) needs to be converted to linear to prevent sharp fall off into caves etc
          float skyLevel = clamp(vLight.a / ${MAX_LIGHT_LEVEL}.0, 0.0, 1.0);
          skyLevel = srgbToLinear(vec3(skyLevel)).r; 

          vec3 sky = skyRGB * skyLevel;       

          vec3 block = vLight.rgb / 255.0;
          block = srgbToLinear(block); // hexToBlockLight is sRGB so we need to convert
          
          // max means only the largest source is used
          vec3 lit = max(sky, block);
          
          // additive allows surface block lights in daytime to still have some effect
          // vec3 lit = sky + block; 

          // enforce a nonzero floor (minFactor) so totally dark isn’t pitch black if desired
          float minFactor = 0.02;
          float maxFactor = 1.0;
          lit = mix(vec3(minFactor), vec3(maxFactor), lit);

          float dim = vDim / 255.0;

          vec3 adjustedColor = mainColor.rgb * lit * aoFactor * dim;
          csm_DiffuseColor   = vec4(adjustedColor, mainColor.a);
        }
          
      `,
    })

    this.damageTexture = this.textureLoader.load('/damage.png')
    this.damageTexture.flipY = false
    this.damageTexture.magFilter = THREE.NearestFilter
    this.damageTexture.minFilter = THREE.NearestFilter
    this.damageTexture.colorSpace = THREE.SRGBColorSpace
  }

  getDamageMaterial() {
    const material = new CustomShaderMaterial({
      baseMaterial: THREE.MeshBasicMaterial,
      map: this.damageTexture,
      side: THREE.FrontSide,
      // alphaTest: ALPHA_TEST,
      // color: 'white',
      transparent: true,
      // depthWrite: false,
      // blending: THREE.MultiplyBlending,
      polygonOffset: true,
      polygonOffsetUnits: -1, // more negative = more bias toward camera
      polygonOffsetFactor: -1,
      uniforms: {
        ...this.skyUniforms,
        uDamage: { value: 0 },
      },
      vertexShader: `
        uniform float uElapsed;
        uniform float uWindStrength;
        uniform vec2 uWindDirection;
        
        varying vec2 vUv;
        varying float vWind;
        
        attribute float wind;        

        void main() {
          vUv = uv;

          vWind = wind / 255.0;

          // wind displacement
          if (vWind > 0.0) {
            // use world position for phase variation (so adjacent blocks don't sync)
            vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
            // vec3 worldPos = position + vec3(modelMatrix[3][0], modelMatrix[3][1], modelMatrix[3][2]);
            
            // phase based on world XZ so neighboring grass sways differently
            float phase = worldPos.x * 0.5 + worldPos.z * 0.3 + uElapsed * 2.0;
            
            // main sway
            float sway = sin(phase) * 0.5 + sin(phase * 2.3 + 0.5) * 0.25;
            
            // apply displacement scaled by wind weight
            vec2 offset = normalize(uWindDirection) * sway * uWindStrength * vWind;
            
            csm_Position = position + vec3(offset.x, 0.0, offset.y);
          }
        }
      `,
      fragmentShader: `
        uniform float uDamage;
        varying vec2 vUv;
        void main() {
          // 8 tiles, each 32x32 in a 256x32 texture
          float numTiles = 8.0;
          
          // Convert damage (0-1) to tile index (0-9)
          float tileIndex = floor(uDamage * (numTiles - 0.001));
          
          // Scale U to tile width (0.1 of texture) and offset to correct tile
          vec2 uv = vUv;
          uv.x = (uv.x + tileIndex) / numTiles;
          
          csm_DiffuseColor = texture2D(map, uv);
          
          // vec4 texColor = texture2D(map, uv);
          // csm_DiffuseColor = vec4(texColor.rgb, texColor.a * uDamage);
        }
      `,
    })
    return material
    // const material = this.damage.clone()
    // material.uniforms = {
    //   ...this.damage.uniforms,
    //   uDamage: { value: 0 },
    // }
    // return material
  }

  update({ width, height, pad, maxSlices, lookupArray, bitmaps, canvases }) {
    const resized = this.width !== width || this.height !== height

    this.width = width
    this.height = height
    this.pad = pad
    this.maxSlices = maxSlices

    this.uniforms.uAtlasSize.value.set(width, height)
    this.uniforms.uPadPx.value = pad
    this.uniforms.uLookupWidth.value = maxSlices

    // console.log('UPDATE', { width, height, pad, maxSlices, lookupArray, bitmaps, canvases })

    const cans = bitmaps || canvases

    if (resized) {
      this.texture.dispose()
      this.texture = new THREE.Texture(cans[0])
      this.texture.flipY = false // no-flip, we work in image space everywhere
      this.texture.generateMipmaps = false
      this.texture.mipmaps = cans
      this.texture.anisotropy = this.anisotropy
      this.texture.magFilter = THREE.NearestFilter
      this.texture.minFilter = this.minFilter
      this.texture.wrapS = this.texture.wrapT = THREE.ClampToEdgeWrapping
      this.texture.colorSpace = THREE.SRGBColorSpace
      this.uniforms.uAtlas.value = this.texture
    } else {
      this.texture.image = cans[0]
      this.texture.mipmaps = cans
    }
    this.texture.needsUpdate = true

    // TODO: only dispose if arr size changed?

    this.lookupTex.dispose()
    // this.lookupTex.image = { data: lookupArray, width: maxSlices, height: 1 }
    // this.lookupTex.source.data = lookupArray
    this.lookupTex.image.data = lookupArray
    this.lookupTex.image.width = maxSlices
    // this.lookupTex.image.height = 1
    this.lookupTex.needsUpdate = true
    // this.lookupTex = new THREE.DataTexture(lookupArray, maxSlices, 1, THREE.RGBAFormat, THREE.FloatType)
    // this.lookupTex.needsUpdate = true
    // this.lookupTex.minFilter = THREE.NearestFilter
    // this.lookupTex.magFilter = THREE.NearestFilter
    // this.lookupTex.wrapS = THREE.ClampToEdgeWrapping
    // this.lookupTex.wrapT = THREE.ClampToEdgeWrapping
    // this.uniforms.uLookupTex.value = this.lookupTex

    // const c = document.createElement('canvas')
    // c.width = width
    // c.height = height
    // const ct = c.getContext('2d')
    // ct.drawImage(cans[0], 0, 0)
    // document.body.appendChild(c)
    // c.style = 'position:absolute; top:0; left:0; z-index:9999;'
  }

  showOnScreen() {
    const c = document.createElement('canvas')
    c.width = this.width
    c.height = this.height
    const ct = c.getContext('2d')
    ct.drawImage(this.texture.mipmaps[0], 0, 0)
    document.body.appendChild(c)
    c.style = 'position:absolute; top:0; left:0; z-index:9999; pointer-events:none;'
  }

  async download(options = {}) {
    const {
      prefix = 'atlas', // filename prefix
      delayMs = 60, // tiny delay between downloads to keep browsers happy
    } = options

    // helper: save a Blob via an anchor click
    const saveBlob = (filename, blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      // give the browser a moment before revoking
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }

    // helper: promisified toBlob with dataURL fallback
    const canvasToPngBlob = source => {
      return new Promise((resolve, reject) => {
        if (source instanceof ImageBitmap) {
          const c = document.createElement('canvas')
          c.width = source.width
          c.height = source.height
          const ct = c.getContext('2d')
          ct.drawImage(source, 0, 0)
          source = c

          // ???
        }
        if (source.toBlob) {
          source.toBlob(blob => {
            if (blob) resolve(blob)
            else reject(new Error('toBlob returned null'))
          }, 'image/png')
        } else {
          try {
            const dataUrl = source.toDataURL('image/png')
            // Turn dataURL into a Blob without fetching external resources
            const byteString = atob(dataUrl.split(',')[1])
            const len = byteString.length
            const bytes = new Uint8Array(len)
            for (let i = 0; i < len; i++) bytes[i] = byteString.charCodeAt(i)
            resolve(new Blob([bytes], { type: 'image/png' }))
          } catch (e) {
            reject(e)
          }
        }
      })
    }

    // Download all mip levels from 0..N-1
    for (let l = 0; l < this.texture.mipmaps.length; l++) {
      const c = this.texture.mipmaps[l]
      if (!c) continue

      try {
        const blob = await canvasToPngBlob(c)
        const filename = `${prefix}_mip${l}_${c.width}x${c.height}.png`
        saveBlob(filename, blob)
        // throttle slightly to avoid some browsers skipping clicks
        if (delayMs > 0) await new Promise(r => setTimeout(r, delayMs))
      } catch (err) {
        console.error(`[AtlasRenderer] Failed to export mip ${l}:`, err)
      }
    }
  }
}
